import { observer } from "mobx-react-lite";
import { useLocalization } from "@progress/kendo-react-intl";
import { useStores } from "src/stores";
import { useEffect, useRef, useState } from "react";
import { useServices } from "src/services";
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridDataStateChangeEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { Button, ToolbarSpacer } from "@progress/kendo-react-buttons";
import { useHistory } from "react-router";
import { Dealer } from "src/utils/classes";
import { DataResult, process, State } from "@progress/kendo-data-query";
import {
  ColumnMenu,
  ColumnMenuCheckboxFilter,
} from "src/components/GridColumnMenus";
import {
  ExcelExport,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import moment from "moment-timezone";
import { set } from "mobx";

interface ExportColumnDef {
  key: string;
  field: string;
  title: string;
}

const WaitlistScreen = () => {
  const localizationService = useLocalization();

  const { ui } = useStores();
  const { d4ur } = useServices();
  let history = useHistory();

  const [dataState, setDataState] = useState<State>({
    filter: {
      logic: "and",
      filters: [
        {
          logic: "or",
          filters: [
            { field: "activeGridCell", operator: "eq", value: "Active" },
          ],
        },
      ],
    },
    sort: [
      // {
      //   field: "status",
      //   dir: "asc",
      // },
      // {
      //   field: "invited_off_waitlist",
      //   dir: "asc",
      // },
      {
        field: "waitlisted",
        dir: "asc",
      },
    ],
  });

  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [copied, setCopied] = useState<boolean>(false);
  const [waitlistData, setWaitlistData] = useState<WaitlistData>({});
  const [dataResult, setDataResult] = useState<DataResult>(
    process(dealers, dataState)
  );
  const [localInvitesCopy, setLocalInvitesCopy] = useState<any[]>([]);

  const processDealers = (dealers: any[]): Dealer[] => {
    if (!Array.isArray(dealers)) {
      return [];
    }
    // filter out dealers that are not waitlisted 


    const waitlistedDealers = dealers
      .filter((d) => d.waitlisted !== null);

    return Array.from(new Set(waitlistedDealers.map((d) => d.dealer_id)))
      .map((dealerId) => waitlistedDealers.find((d) => d.dealer_id === dealerId))
      .map((d) => new Dealer(d));
    return Array.from(new Set(
      dealers
        .filter((d) => d.waitlisted !== null)
        .map((d) => new Dealer(d))
    ));
  };

  useEffect(() => {
    // ui.setIsGettingDealers(true);
    d4ur.getDealers().then((ret) => {
      const processedDealers = processDealers(
        (ret as D4URxFetchResult).result || []
      );
      // ui.setIsGettingDealers(false);
      d4ur.getWaitlistData().then((ret) => {
        setWaitlistData((ret as D4URxFetchResult).result as WaitlistData);
        setDealers(processedDealers);
        setDataResult(process(processedDealers, dataState));
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localInvitesCopy]);

  const DealerActionsCell = (props: GridCellProps) => {
    const { dealer_id } = props.dataItem as Dealer;

    return (
      <td>
        <Button
          className="k-button k-primary"
          onClick={() => {
            history.push(`/retailer/${dealer_id}`);
          }}
        >
          Edit
        </Button>
      </td>
    );
  };

  const waitlistedAtCell = (props: GridCellProps) => {
    const { waitlisted } = props.dataItem as Dealer;

    return (
      <td>
        {waitlisted
          ? moment(waitlisted)
            .subtract(4, "hours")
            .format("MMM Do, YYYY [at] h:mmA")
          : ""}
      </td>
    );
  };

  const dealerEmailCell = (props: GridCellProps) => {
    const { contact_email } = props.dataItem as Dealer;

    return (
      <td>
        <p
          style={{
            cursor: "pointer", whiteSpace: 'normal', wordWrap: 'break-word', fontSize: '14px'
          }}
          onClick={() => {
            navigator.clipboard.writeText(contact_email);
            setCopied(true);
            setTimeout(() => {
              setCopied(false);
            }, 2500);
          }}
        >
          {contact_email}
        </p>
      </td >
    );
  };

  const eventRequestedColumn = (props: GridCellProps) => {
    const { event_requested, event_requested_at, season_id } = props.dataItem as Dealer;
    // if (season_id !== ui.currentSeason.season_id) {
    //   return <td>N/A</td>;
    // }
    if (event_requested && season_id === ui.currentSeason.season_id) {
      return (
        <td>{`YES at ${moment(event_requested_at)
          .subtract(4, "hours")
          .format("MMM Do, YYYY [at] h:mmA")}`}</td>
      );
    }
    return <td>No</td>;
  };

  const eventStatusColumn = (props: GridCellProps) => {
    const { status, season_id } = props.dataItem as Dealer;
    if (season_id !== ui.currentSeason.season_id) {
      return <td>N/A</td>;
    }
    if (status === "approved") {
      return (
        <td style={{ color: "lightgreen" }}>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "auto",
            }}
          >
            YES
          </div>
        </td>
      );
    } else if (status === "new") {
      return <td>AWAITING APPROVAL</td>;
    }
    return <td>{status?.toUpperCase()}</td>;
  };

  const inviteCell = (props: GridCellProps) => {
    const { invited_off_waitlist, event_requested, season_id, status } = props.dataItem as Dealer;

    const handleInviteOffWaitlist = (e: any) => {
      return d4ur
        .inviteOffWaitlist(
          (props.dataItem as Dealer).dealer_id,
          e.target.checked
        )
        .then(() => {
          return d4ur.getDealers().then((ret) => {
            const processedDealers = processDealers(
              (ret as D4URxFetchResult).result || []
            );
            setDealers(processedDealers);
            setDataResult(process(processedDealers, dataState));
          });
        });
    };

    return (
      <td>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "auto",
          }}
        >
          <input
            type="checkbox"
            checked={invited_off_waitlist !== null}
            onChange={handleInviteOffWaitlist}
            disabled={event_requested && status !== 'ineligible' && (season_id === ui.currentSeason.season_id)}
            style={{ width: "30px", height: "30px", cursor: "pointer" }}
          />
          {invited_off_waitlist
            ? `Marked as invited at ${moment(invited_off_waitlist)
              .subtract(4, "hours")
              .format("MMM Do, YYYY [at] h:mmA")}`
            : ""}
        </div>
      </td>
    );
  };

  const DealerNotesCell = (props: GridCellProps) => {
    const { dealer_notes } = props.dataItem as Dealer;

    if (!dealer_notes) {
      return <td></td>;
    }

    let parsedNotes = [];
    try {
      if (typeof dealer_notes === 'string') {
        // Clean up the string and check if it's valid JSON
        const cleanedNotes = dealer_notes.trim();
        if (cleanedNotes === '' || cleanedNotes === 'null' || cleanedNotes === 'undefined' || cleanedNotes === '[]') {
          return <td></td>;
        } else if (cleanedNotes.startsWith('[') || cleanedNotes.startsWith('{')) {
          parsedNotes = JSON.parse(cleanedNotes);
        } else {
          console.warn('dealer_notes not in JSON format:', cleanedNotes);
          return <td></td>;
        }
      } else if (Array.isArray(dealer_notes)) {
        parsedNotes = dealer_notes;
      } else {
        // Convert to string and try parsing
        const noteString = dealer_notes.toString();
        if (noteString.startsWith('[') || noteString.startsWith('{')) {
          parsedNotes = JSON.parse(noteString);
        } else {
          console.warn('dealer_notes not in valid format:', dealer_notes);
          return <td></td>;
        }
      }
    } catch (e) {
      console.error('Failed to parse dealer_notes in Waitlist:', e);
      return <td></td>;
    }

    if (!Array.isArray(parsedNotes) || parsedNotes.length === 0) {
      return <td></td>;
    }

    const formattedNotes = parsedNotes
      .sort((a: any, b: any) => moment(a.created_at).diff(moment(b.created_at)))
      .map((note: any) => {
        const formattedDateTime = moment(note.created_at).format(
          "MMM DD, YYYY hh:mm A"
        );
        return `${formattedDateTime} - ${note.created_by}: ${note.note}`;
      });

    return (
      <td style={{ overflowY: "auto" }}>
        {formattedNotes.map((note: string, index: number) => (
          <div key={index} style={{ borderBottom: "1px solid lightgrey" }}>
            {note}
          </div>
        ))}
      </td>
    );
  };

  const _export = useRef<ExcelExport | null>(null);
  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const exportColumnDefs: ExportColumnDef[] = [
    { key: 'dealer_id', field: 'dealer_id', title: 'Lat. Dealer ID' },
    { key: 'parts_code', field: 'parts_code', title: 'Sales Code' },
    { key: 'dealer_name', field: 'dealer_name', title: 'Dealer Name' },
    { key: 'contact_email', field: 'contact_email', title: 'Contact Email' },
    { key: 'waitlisted', field: 'waitlisted', title: 'Waitlisted At' },
    { key: 'first_time_dealer', field: 'first_time_dealer', title: '1st Time Dealer' },
    { key: 'hispanic', field: 'hispanic', title: 'Hispanic' },
    { key: 'invited_off_waitlist', field: 'invited_off_waitlist', title: 'Invited Off Waitlist At' },
    { key: 'event_requested_at', field: 'event_requested_at', title: 'Event Requested At' },
    { key: 'status', field: 'status', title: 'Event Status' },
  ];

  const [selectedExportColumns, setSelectedExportColumns] = useState<string[]>(
    exportColumnDefs.map((col) => col.key)
  );
  const toggleExportColumn = (key: string) => {
    setSelectedExportColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };
  const selectAllExportColumns = () => setSelectedExportColumns(exportColumnDefs.map((col) => col.key));
  const clearAllExportColumns = () => setSelectedExportColumns([]);
  const selectedExportColumnDefs = exportColumnDefs.filter((col) => selectedExportColumns.includes(col.key));

  const gridContentRef = useRef<HTMLDivElement | null>(null);
  const topScrollbarRef = useRef<HTMLDivElement | null>(null);
  const [scrollbarWidth, setScrollbarWidth] = useState<number>(3000);

  useEffect(() => {
    if (dealers.length === 0) return;
    const measureWidth = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && gridContent.scrollWidth > 0) setScrollbarWidth(gridContent.scrollWidth);
    };
    const syncTopScrollbar = () => {
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent && topScrollbarRef.current) topScrollbarRef.current.scrollLeft = gridContent.scrollLeft;
    };
    requestAnimationFrame(() => {
      measureWidth();
      setTimeout(measureWidth, 200);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.addEventListener('scroll', syncTopScrollbar);
    });
    window.addEventListener('resize', measureWidth);
    return () => {
      window.removeEventListener('resize', measureWidth);
      const gridContent = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement;
      if (gridContent) gridContent.removeEventListener('scroll', syncTopScrollbar);
    };
  }, [dealers.length, dataResult.data.length]);

  const filterExportData = (data: any[]) => {
    // loop through data, and if data.event_requested_at is prior than 2024, set that to null
    data.forEach((dealer) => {
      if (dealer.event_requested_at && moment(dealer.event_requested_at).isBefore('2024-01-01')) {
        dealer.event_requested_at = null;
      }
    });
    // sort data by dealer.id ascending
    data.sort((a, b) => a.dealer_id - b.dealer_id);
    // if waitlisted, set event.status to waitlisted
    data.forEach((dealer) => {
      if (dealer.invited_off_waitlist && !dealer.event_requested_at) {
        dealer.status = 'invited';
      }
      if ((dealer.status === 'approved' && !dealer.event_requested_at) || !dealer.status) {
        dealer.status = 'waitlisted';
      }
    });
    return data.map((d: any) => {
      return {
        ...d,
        first_time_dealer: d.first_time_dealer ? 'YES' : null,
        hispanic: d.hispanic ? 'YES' : null,
      };
    });
  }

  const rowRender = (row, props) => {
    let color = "#fff";
    if (props.dataItem.status === "approved" && props.dataItem.season_id === ui.currentSeason.season_id) {
      color = "grey";
    }
    let style = {
      ...row.props.style,
      backgroundColor: color,
      borderColor: "black",
      borderWidth: 2,
    };
    return <tr {...row.props} style={style}></tr>;
  };

  const handleCloseWaitlist = () => {
    console.log("Closing waitlist");
    try {
      d4ur.closeWaitlist().then((ret) => {
        console.log("Waitlist closed", ret);
        d4ur.getWaitlistData().then((ret) => {
          setWaitlistData((ret as D4URxFetchResult).result as WaitlistData);
        });
      });
    } catch (e) {
      console.error("Error closing waitlist", e);
    }
  }

  return (
    <>
      <div id="Dashboard" className="waitlist-page main-content">
        <div className="card-container grid">
          <div>
            <h3 className="card-title">
              {localizationService.toLanguageString(
                "custom.waitlist",
                "waitlist"
              )}
            </h3>
            <p>
              Waitlist Enabled: {waitlistData.waitlistEnabled ? "Yes" : "No"}
            </p>
            <div>
              <Button
                primary={true}
                disabled={!waitlistData.waitlistEnabled || waitlistData.waitlistClosed}
                onClick={handleCloseWaitlist}
              >
                {waitlistData.waitlistClosed ? "Waitlist Closed" : "Close Waitlist"}
              </Button>
              <p style={{ fontSize: "0.9em", marginTop: "5px", color: "#666" }}>
                Close the waitlist to prevent any more dealers from joining it. You will still be able to invite previously waitlisted dealers.
              </p>
            </div>
            <p>
              Total Waitlist Count: {dealers.length}
            </p>
            <p>
              Total Invited Off Waitlist: {dealers.filter(dealer => dealer.invited_off_waitlist !== null).length}
            </p>
            <p>
              Total Waiting To Be Invited: {dealers.length - dealers.filter(dealer => dealer.invited_off_waitlist !== null).length}
            </p>
            <br />
            <br />
            <p
              style={{ cursor: "pointer" }}
              onClick={() => {
                navigator.clipboard.writeText(
                  "https://enroll.driveforyourcommunity.com/eligibility"
                );
                setCopied(true);
                setTimeout(() => {
                  setCopied(false);
                }, 2500);
              }}
            >
              Event Request Form URL (Click to Copy):
              https://enroll.driveforyourcommunity.com/eligibility
            </p>
            {copied && <p style={{ color: "green" }}>Copied to clipboard!</p>}
          </div>
          {/* <p>Click the checkbox in the "Invited off Waitlist" column to mark a dealer as invited</p> */}
          {/* <Button primary={!ui.events.length} onClick={handleEventRequestButton}>
                        { ui.events.length ?
                            localizationService.toLanguageString('custom.requestNewEvent','requestNewEvent') :
                            localizationService.toLanguageString('custom.beginHere','beginHere')
                        }
                    </Button> */}
          <div className="card-component">
            <ExcelExport
              data={filterExportData(dataResult.data)}
              ref={_export}
              fileName={`dealers_export_${moment().format("YYYYMMDD-hhmma")}.xlsx`}
            >
              {selectedExportColumnDefs.map((col) => (
                <ExcelExportColumn key={col.key} field={col.field} title={col.title} />
              ))}
            </ExcelExport>
            <div style={{ marginBottom: '12px' }}>
              <details>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
                  Choose Excel columns ({selectedExportColumnDefs.length} selected)
                </summary>
                <div style={{ marginTop: '8px' }}>
                  <button className="k-button k-button-md k-rounded-md" onClick={selectAllExportColumns} type="button" style={{ marginRight: '8px' }}>Select All</button>
                  <button className="k-button k-button-md k-rounded-md" onClick={clearAllExportColumns} type="button">Clear All</button>
                </div>
                <div style={{ marginTop: '10px', display: 'grid', gridTemplateColumns: 'repeat(3, minmax(220px, 1fr))', gap: '6px 12px' }}>
                  {exportColumnDefs.map((col) => (
                    <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <input type="checkbox" checked={selectedExportColumns.includes(col.key)} onChange={() => toggleExportColumn(col.key)} />
                      <span>{col.title}</span>
                    </label>
                  ))}
                </div>
              </details>
            </div>

            <style>{`
              .waitlist-grid-wrapper .k-grid-content { overflow-x: scroll; scrollbar-width: none; -ms-overflow-style: none; }
              .waitlist-grid-wrapper .k-grid-content::-webkit-scrollbar { display: none; }
              .waitlist-top-scrollbar { overflow-x: scroll; overflow-y: hidden; height: 14px; position: sticky; top: 0; z-index: 50; background: #f5f5f5; border-bottom: 1px solid #ddd; }
              .waitlist-top-scrollbar::-webkit-scrollbar { height: 8px; }
              .waitlist-top-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; }
              .waitlist-top-scrollbar::-webkit-scrollbar-thumb { background: #aaa; border-radius: 4px; }
              .waitlist-top-scrollbar::-webkit-scrollbar-thumb:hover { background: #666; }
            `}</style>
            <div style={{ height: '70vh', overflowY: 'auto', overflowX: 'hidden' }}>
              <div ref={topScrollbarRef} className="waitlist-top-scrollbar" onScroll={(e) => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) gc.scrollLeft = e.currentTarget.scrollLeft; }}>
                <div style={{ width: scrollbarWidth, height: 1 }} />
              </div>
              <div ref={gridContentRef} className="waitlist-grid-wrapper">
            <Grid
              data={dataResult}
              rowRender={rowRender}
              sortable={{ mode: "multiple" }}
              {...dataState}
              onDataStateChange={(event: GridDataStateChangeEvent) => {
                setDataResult(process(dealers, event.dataState));
                setDataState(event.dataState);
              }}
              resizable={true}
              onColumnResize={() => { const gc = gridContentRef.current?.querySelector('.k-grid-content') as HTMLElement; if (gc) setScrollbarWidth(gc.scrollWidth); }}
            >
              <GridToolbar>
                <button className="k-button k-button-md k-rounded-md k-button-solid" onClick={() => setDataState({
                  filter: {
                    logic: "and",
                    filters: [
                      {
                        logic: "or",
                        filters: [
                          { field: "activeGridCell", operator: "eq", value: "Active" },
                        ],
                      },
                    ],
                  },
                })
                }>Clear All Sorting</button>
                <ToolbarSpacer />
                <button
                  className="k-button k-button-md k-rounded-md k-button-solid"
                  onClick={excelExport}
                  disabled={selectedExportColumnDefs.length === 0}
                >
                  Export ({selectedExportColumnDefs.length} columns)
                </button>
              </GridToolbar>
              <GridColumn
                field="dealer_name"
                title={localizationService.toLanguageString(
                  "custom.dealer",
                  "dealer"
                )}
                columnMenu={ColumnMenu}
                width={150}
              />
              <GridColumn
                field="contact_email"
                title="Dealer Email (click to copy)"
                columnMenu={ColumnMenu}
                cell={dealerEmailCell}
                width={250}
              />
              <GridColumn
                field="waitlisted"
                title="Joined Waitlist"
                cell={waitlistedAtCell}
                columnMenu={ColumnMenu}
                width={200}
              />
              <GridColumn
                field="invited_off_waitlist"
                title="Invited Off Waitlist"
                cell={inviteCell}
                columnMenu={ColumnMenu}
                width={200}
              />
              <GridColumn
                field="event_requested"
                title="Event Requested"
                columnMenu={ColumnMenu}
                cell={eventRequestedColumn}
                width={200}
              />
              <GridColumn
                field="firstTimeDealerGridCell"
                title="1st Time Dealer"
                columnMenu={ColumnMenu}
                // cell={firstTimeDealerColumn}
                width={200}
              />
              <GridColumn
                field="hispanicReadable"
                title="Hispanic"
                columnMenu={ColumnMenu}
                // cell={hispanicColumn}
                width={200}
              />
              <GridColumn
                field="status"
                title="Event Approved"
                columnMenu={ColumnMenu}
                cell={eventStatusColumn}
                width={200}
              />
              {/* <GridColumn
                field="created_timestamp"
                title="Signup Date"
                format="{0:E, MMM dd yyyy}"
                filter="date"
                columnMenu={ColumnMenu}
              />
              <GridColumn
                field="approved_dealers"
                title="Allowed"
                columnMenu={ColumnMenuCheckboxFilter(dealers, {
                  searchBox: () => null,
                })}
              />
              <GridColumn
                field="activeGridCell"
                title="Status"
                columnMenu={ColumnMenuCheckboxFilter(dealers, {
                  searchBox: () => null,
                })}
              /> */}
              <GridColumn title="Dealer Notes" cell={DealerNotesCell} width={350} />
              <GridColumn cell={DealerActionsCell} width={300} />
            </Grid>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(WaitlistScreen);
